import { useEffect, useState } from 'react';

import { Band, Eyebrow, Lead, MonoMeta, SectionTitle } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';
import { useVisibilityGate } from '@/lib/visibility-gate';

import { STEP_IDS, StepInstall, StepLearn, StepWork } from './how-steps';
import { StepList } from './StepList';

import type { ReactElement } from 'react';

/**
 * "How it works" — three steps, one stage.
 *
 * The band's argument is in the step LABELS: step 01 is tagged "yours" and
 * steps 02 and 03 are tagged "ours". A visitor evaluating an integration is
 * really asking "how much of my quarter does this cost", so the split of
 * labour is the headline, and the stage then shows what each step actually
 * looks like.
 *
 * Steps advance on their own every 6.5s and stop permanently the moment the
 * visitor picks one — an auto-advance that keeps fighting a deliberate choice
 * is the most irritating pattern a carousel has.
 */

const AUTO_MS = 6500;

export function HowItWorks(): ReactElement {
  const { t } = useT();
  const [step, setStep] = useState(0);
  const [auto, setAuto] = useState(true);
  const view = useVisibilityGate<HTMLDivElement>();

  useEffect(() => {
    // `view.active` is a dependency, not a check inside the tick: an interval
    // that fires and returns early still wakes the main thread every 6.5s for
    // the whole session. Clearing it is the point.
    if (!auto || !view.active) return undefined;
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;
    const tick = setInterval(() => {
      setStep((s) => (s + 1) % STEP_IDS.length);
    }, AUTO_MS);
    return () => {
      clearInterval(tick);
    };
  }, [auto, view.active]);

  const choose = (index: number): void => {
    setAuto(false);
    setStep(index);
  };

  return (
    // The page ground, and no panel of its own: this section used to be a dark
    // inset card sitting flush against the black story band above it, and #000
    // against #08090c is not a boundary anyone can see — only the rounded notch
    // was, which made it read as a seam rather than as a section. Going light
    // makes the boundary the thing it should have been: a change of ground,
    // with the demo above kept as the page's one dark passage.
    //
    // No `ambient`: that wash is dark-tone decoration and `Band` already
    // ignores it on a light ground, so passing it would only look like it did
    // something.
    //
    // `align="card"` is REQUIRED and is invisible if you forget it — an inset
    // tone sits on the card rule automatically, so dropping the panel also
    // drops the rule and slides this title 24px left of its neighbours.
    // `LandingPage.test.tsx` fails on exactly that.
    <Band
      tone="page"
      id="how"
      space="lg"
      align="card"
      // The leading space the section owns, on top of its own rhythm — room
      // between the story demo and this. On the OUTER element so it extends
      // the ground rather than the column.
      outerClassName="pt-8 sm:pt-12"
      aria-labelledby="how-title"
    >
      <div className="relative max-w-[730px]">
        <Eyebrow>{t('home.how.eyebrow')}</Eyebrow>
        <SectionTitle id="how-title" className="mt-3">
          {t('home.how.title')}
        </SectionTitle>
        <Lead className="mt-4">{t('home.how.lead')}</Lead>
      </div>

      <div
        ref={view.ref}
        className="relative mt-9 grid items-start gap-8 lg:grid-cols-[minmax(280px,368px)_1fr]"
      >
        {/* The steps. A tablist would promise arrow-key semantics that the
            auto-advance breaks, so these are plain buttons that set state and
            the stage is a plain region — honest about what it is. */}
        {/* `self-stretch` rather than dropping the grid's `items-start`: only
            THIS column should fill the row. The stage opposite keeps its
            content height, so a locale that makes the steps taller than 566px
            cannot stretch the panel past the stage inside it and leave a band
            of empty white under the demo. */}
        <StepList step={step} choose={choose} t={t} />

        {/* The stage, over its backlight.
            The wrapper exists only to give the two a shared positioning
            context: both are positioned, so DOM order alone decides that the
            light paints under the panel — no z-index, and nothing to keep in
            step if a third layer ever lands between them. */}
        {/* `min-w-0` is load-bearing, not tidiness. A grid item's automatic
            minimum size is its MIN-CONTENT width unless it scrolls, and the
            stage used to be the grid item itself — its `overflow-hidden`
            resolved that minimum to 0, which is what let the `1fr` column
            shrink it. Wrapping it moved the item to this div, whose overflow
            is visible, so the column stopped shrinking and the panel overhung
            the band's right edge (measured at 1440px: clipped mid-sentence,
            and the step column squeezed hard enough to re-wrap its headings). */}
        {/* No `Backlight` here any more: it existed to stop a white panel
            reading as a hole punched through a dark ground, and on the page
            ground there is no hole to fill — a brand-tinted pool under a white
            card on white would just be a smudge. The story band keeps its one.

            The panel's edge changes for the same reason. Its ring was
            `rgba(255,255,255,.09)`, which only separates a white card from a
            DARK band; on white it is invisible, so the panel needs a real line
            and a softer shadow to sit above the page at all. */}
        <div className="relative min-w-0">
          <div className="relative flex min-h-[566px] flex-col overflow-hidden rounded-[18px] border border-[color:var(--bd-line-3)] bg-[color:var(--bd-page)] shadow-[0_1px_2px_rgba(12,13,16,.04),0_14px_32px_-14px_rgba(12,13,16,.16),0_48px_90px_-56px_rgba(12,13,16,.34)]">
            <div className="flex items-center justify-between gap-4 border-b border-[color:var(--bd-line)] bg-[color:var(--bd-raise)] px-5 py-3">
              <MonoMeta className="!tracking-[0.14em] rtl:!tracking-normal">
                {t(`home.how.${STEP_IDS[step]}.stage`)}
              </MonoMeta>
              <div className="flex items-center gap-3">
                <MonoMeta>0{step + 1} / 03</MonoMeta>
                <div className="flex gap-1.5">
                  {/* The dot is an inner span so the BUTTON can carry a real
                    touch target while the pill stays 6px.

                    Both halves of WCAG 2.5.8 failed here and only the second
                    one is fixable by growing the control: three 6×6 dots on a
                    6px gap put their centres 12px apart, and two targets 12px
                    apart cannot BOTH satisfy the 24px-circle spacing rule at
                    any size. So the button itself becomes 24×24 and the row
                    separates to 30px between centres.

                    Scoped to `pointer: coarse` deliberately. It is a touch
                    requirement; on a mouse the 6px pill is a correct, deliberate
                    progress indicator, and inflating it there would change a
                    design that is right — and move every desktop visual
                    baseline for a problem no mouse user has. */}
                  {STEP_IDS.map((id, i) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => {
                        choose(i);
                      }}
                      aria-label={t('home.how.showStep', { n: String(i + 1) })}
                      // The row is a set of controls where one is current, and it
                      // said so to nobody: a screen-reader user got three
                      // identical "show step N" buttons with no indication which
                      // step they were on. It also makes the correct absence of a
                      // hover cue on the selected dot legible to the hover gate,
                      // which exempts a control that declares itself current
                      // rather than one that appears on a list.
                      aria-current={i === step ? 'step' : undefined}
                      className="group grid place-items-center [@media(pointer:coarse)]:size-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)] focus-visible:ring-offset-2"
                    >
                      <span
                        aria-hidden="true"
                        className={[
                          'block h-1.5 rounded-full transition-all duration-500',
                          i === step
                            ? 'w-[22px] bg-[color:var(--bd-ink)]'
                            : // The dot is the only control in this section that
                              // offered no hover cue at all, so a pointer user
                              // had nothing telling them the row is operable.
                              // The SELECTED dot needs none — it is already the
                              // wide, inked one.
                              'w-1.5 bg-[color:var(--bd-dot)] group-hover:bg-[color:var(--bd-muted)]',
                        ].join(' ')}
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden">
              <div
                // `translateX` is PHYSICAL, and this track is not. Under RTL the
                // flex panels lay out leftward from the viewport (01 rightmost),
                // so advancing needs a POSITIVE translate — the signed literal
                // that used to live here pushed every panel off the left edge
                // instead, and steps 02 and 03 rendered as a blank white panel
                // for every Arabic and Hebrew visitor. The direction lives in
                // CSS because that is the only layer that knows it: a JS read of
                // `dir` is wrong for a frame on hydration, and wrong forever in
                // a subtree that sets its own direction.
                className="flex h-full w-[300%] transition-transform duration-700 ease-[cubic-bezier(.16,1,.3,1)] [--sx-track:-1] rtl:[--sx-track:1]"
                style={{
                  transform: `translateX(calc(${(step * 33.3333).toFixed(4)}% * var(--sx-track)))`,
                }}
              >
                <StepInstall />
                <StepLearn />
                {/* Gated as well as stepped: the step interval stops offscreen,
                  so a visitor who scrolls away while step 03 is showing would
                  otherwise leave the chat simulator typing — a `setInterval`
                  per character — for the rest of the session. */}
                <StepWork active={step === 2 && view.active} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Band>
  );
}
